import { Component, OnInit } from '@angular/core';
import { Clipboard } from '@angular/cdk/clipboard';
import { MatTableDataSource } from '@angular/material/table';
import { InvoiceInterface } from '@contracts/interfaces/invoice.interface';
import { LineItem } from '@contracts/interfaces/lineitem.interface';
import { Transaction } from '@contracts/interfaces/transaction.interfaces';
import { ACCOUNT } from '../../accounts';
import { InvoicesService } from '../../services/invoices.service';
import { TransactionsService } from '../../services/transactions.service';

interface WeeklyReportGroup {
  label: string;
  count: number;
  amount: number;
  gst: number;
}

interface WeeklyReportSummary {
  revenue: number;
  revenueGst: number;
  expenses: number;
  expensesGst: number;
  wages: number;
  profit: number;
  transactionCount: number;
}

@Component({
  selector: 'ap-weekly-report',
  templateUrl: './weekly-report.component.html',
  styleUrls: ['./weekly-report.component.scss']
})
export class WeeklyReportComponent implements OnInit {
  columnsToDisplay = ['date', 'description', 'account', 'amount', 'gst'];
  dataSource: MatTableDataSource<Transaction> = new MatTableDataSource([]);

  weekStart: Date;
  weekEnd: Date;
  isLoading = false;

  summary: WeeklyReportSummary = this.createEmptySummary();
  expenseGroups: WeeklyReportGroup[] = [];
  revenueGroups: WeeklyReportGroup[] = [];
  copyMessage = '';
  private invoicesByTransactionHash: { [transactionHash: string]: InvoiceInterface[] } = {};

  constructor(
    private transactionService: TransactionsService,
    private invoiceService: InvoicesService,
    private clipboard: Clipboard) { }

  ngOnInit(): void {
    this.weekStart = this.getTuesday(new Date());
    this.weekEnd = this.getMonday(this.weekStart);
    this.loadReport();
  }

  previousWeek() {
    this.weekStart = this.addDays(this.weekStart, -7);
    this.weekEnd = this.getMonday(this.weekStart);
    this.loadReport();
  }

  nextWeek() {
    this.weekStart = this.addDays(this.weekStart, 7);
    this.weekEnd = this.getMonday(this.weekStart);
    this.loadReport();
  }

  currentWeek() {
    this.weekStart = this.getTuesday(new Date());
    this.weekEnd = this.getMonday(this.weekStart);
    this.loadReport();
  }

  loadReport() {
    this.isLoading = true;

    const query: any = {
      'source.account': { $in: [ACCOUNT.REVENUE, ACCOUNT.EXPENSES, ACCOUNT.G72EXPENSES, ACCOUNT.WAGES] },
      ignore: { $in: [null, false] },
      date: {
        $gte: this.weekStart,
        $lte: this.weekEnd
      }
    };

    this.transactionService.find(query, null, null, { date: 1 }).subscribe((transactions) => {
      const sortedTransactions = transactions.sort((a, b) => this.getTime(a.date) - this.getTime(b.date));
      this.dataSource = new MatTableDataSource(sortedTransactions);
      this.buildSummary(sortedTransactions);
      this.loadInvoices(sortedTransactions);
    }, () => {
      this.dataSource = new MatTableDataSource([]);
      this.summary = this.createEmptySummary();
      this.expenseGroups = [];
      this.revenueGroups = [];
      this.invoicesByTransactionHash = {};
      this.isLoading = false;
    });
  }

  loadInvoices(transactions: Transaction[]) {
    const transactionHashes = transactions
      .map(transaction => transaction.source?.sha1)
      .filter((hash): hash is string => !!hash);

    const invoiceNumbers = transactions
      .map(transaction => transaction.invoice?.number || this.getInvoiceNumberFromDescription(transaction.description))
      .filter((number): number is number => typeof number === 'number');

    const queryParts: any[] = [];

    if (transactionHashes.length) {
      queryParts.push({
        'paymentsMade.transactionSha1': { $in: transactionHashes }
      });
    }

    if (invoiceNumbers.length) {
      queryParts.push({
        number: { $in: Array.from(new Set(invoiceNumbers)) }
      });
    }

    if (!queryParts.length) {
      this.invoicesByTransactionHash = {};
      this.isLoading = false;
      return;
    }

    const query = queryParts.length === 1 ? queryParts[0] : { $or: queryParts };

    this.invoiceService.find(query).subscribe((invoices) => {
      this.invoicesByTransactionHash = this.mapInvoicesToTransactions(invoices || [], transactions);
      this.isLoading = false;
    }, () => {
      this.invoicesByTransactionHash = {};
      this.isLoading = false;
    });
  }

  buildSummary(transactions: Transaction[]) {
    const summary = this.createEmptySummary();
    const expenses: { [key: string]: WeeklyReportGroup } = {};
    const revenue: { [key: string]: WeeklyReportGroup } = {};

    for (const transaction of transactions) {
      const amount = transaction.amount || 0;
      const gst = transaction.vat || 0;
      const netAmount = this.getTransactionNetAmount(transaction);

      summary.transactionCount++;

      if (this.isRevenue(transaction)) {
        summary.revenue += netAmount;
        summary.revenueGst += gst;
        this.addGroup(revenue, this.getRevenueLabel(transaction), netAmount, gst);
      }

      if (this.isExpense(transaction)) {
        summary.expenses += netAmount;
        summary.expensesGst += gst;
        this.addGroup(expenses, this.getExpenseLabel(transaction), netAmount, gst);
      }

      if (this.isWage(transaction) && amount < 0) {
        summary.wages += amount;
      }
    }

    summary.profit = summary.revenue + summary.expenses + summary.wages;
    this.summary = summary;
    this.expenseGroups = this.sortGroups(expenses);
    this.revenueGroups = this.sortGroups(revenue);
  }

  addGroup(groups: { [key: string]: WeeklyReportGroup }, label: string, amount: number, gst: number) {
    if (!groups[label]) {
      groups[label] = {
        label,
        count: 0,
        amount: 0,
        gst: 0
      };
    }

    groups[label].count++;
    groups[label].amount += amount;
    groups[label].gst += gst;
  }

  sortGroups(groups: { [key: string]: WeeklyReportGroup }) {
    return Object.values(groups).sort((a, b) => Math.abs(b.amount) - Math.abs(a.amount));
  }

  isRevenue(transaction: Transaction) {
    return transaction.source?.account === ACCOUNT.REVENUE && transaction.amount > 0;
  }

  isExpense(transaction: Transaction) {
    return [ACCOUNT.EXPENSES, ACCOUNT.G72EXPENSES].includes(transaction.source?.account) && transaction.amount < 0;
  }

  isWage(transaction: Transaction) {
    return transaction.source?.account === ACCOUNT.WAGES;
  }

  getAccountLabel(transaction: Transaction) {
    switch (transaction.source?.account) {
      case ACCOUNT.REVENUE:
        return 'Revenue';
      case ACCOUNT.G72REVENUE:
        return 'G72 Revenue';
      case ACCOUNT.CASH_REVENUE:
        return 'Cash Revenue';
      case ACCOUNT.EBAY_REVENUE:
        return 'eBay Revenue';
      case ACCOUNT.EXPENSES:
        return 'Expenses';
      case ACCOUNT.G72EXPENSES:
        return 'G72 Expenses';
      case ACCOUNT.WAGES:
        return 'Wages';
      default:
        return transaction.source?.account || 'Unknown';
    }
  }

  getExpenseLabel(transaction: Transaction) {
    return transaction.supplier?.business || transaction.subcategory || transaction.atocategory || 'Uncategorised';
  }

  getRevenueLabel(transaction: Transaction) {
    if (transaction.revenueChannel?.ebay) {
      return 'eBay';
    }

    if (transaction.revenueChannel?.retail) {
      return 'Retail';
    }

    if (transaction.revenueChannel?.msp) {
      return 'MSP';
    }

    if (transaction.revenueChannel?.outcall) {
      return 'Outcall';
    }

    return this.getAccountLabel(transaction);
  }

  getDescription(transaction: Transaction) {
    if (transaction.supplier?.business) {
      return transaction.supplier.business;
    }

    return transaction.description || transaction.note || '';
  }

  getTransactionNetAmount(transaction: Transaction) {
    return (transaction.amount || 0) - (transaction.vat || 0);
  }

  openTransactionDialog(transaction: Transaction, index: number) {
    if (!transaction?.source?.sha1) {
      return;
    }

    this.transactionService.openTransactionDialog(
      this.transactionService.getTransactionType(transaction),
      transaction.source.sha1,
      index,
      this.dataSource,
      () => this.loadReport()
    );
  }

  copyTransactions() {
    const text = this.buildClipboardReport();
    const copied = this.clipboard.copy(text);
    this.copyMessage = copied ? 'Copied transactions.' : 'Could not copy transactions.';

    setTimeout(() => {
      this.copyMessage = '';
    }, 2500);
  }

  buildClipboardReport() {
    const lines = [
      `Weekly report: ${this.formatDate(this.weekStart)} to ${this.formatDate(this.weekEnd)}`,
      `Revenue net of GST: ${this.formatMoney(this.summary.revenue)} | Expenses net of GST: ${this.formatMoney(this.summary.expenses)} | Wages: ${this.formatMoney(this.summary.wages)} | Net result: ${this.formatMoney(this.summary.profit)}`,
      `Revenue GST: ${this.formatMoney(this.summary.revenueGst)} | Expense GST: ${this.formatMoney(this.summary.expensesGst)}`,
      '',
      'Transactions:',
      'Date | Account | Description | Net amount | GST'
    ];

    for (const transaction of this.dataSource.filteredData || this.dataSource.data || []) {
      lines.push([
        this.formatDate(transaction.date),
        this.getAccountLabel(transaction),
        this.cleanClipboardText(this.getDescription(transaction)),
        this.formatMoney(this.getTransactionNetAmount(transaction)),
        this.formatMoney(transaction.vat || 0)
      ].join(' | '));

      for (const invoice of this.getTransactionInvoices(transaction)) {
        lines.push(`  Invoice #${invoice.number}${invoice.customer?.name ? ` - ${this.cleanClipboardText(invoice.customer.name)}` : ''} | Net total ${this.formatMoney(this.getInvoiceNetTotal(invoice))} | GST ${this.formatMoney(invoice.gst || 0)}`);

        for (const lineItem of invoice.lineItems || []) {
          lines.push(`    - ${this.formatLineItem(lineItem, invoice)}`);
        }
      }
    }

    return lines.join('\n');
  }

  getTransactionInvoices(transaction: Transaction) {
    const invoices = [];
    const seenInvoiceNumbers = new Set<number>();

    if (transaction?.invoice) {
      invoices.push(transaction.invoice);
      seenInvoiceNumbers.add(transaction.invoice.number);
    }

    const descriptionInvoiceNumber = this.getInvoiceNumberFromDescription(transaction.description);
    if (descriptionInvoiceNumber) {
      for (const invoice of this.invoicesByTransactionHash[`invoice:${descriptionInvoiceNumber}`] || []) {
        if (!seenInvoiceNumbers.has(invoice.number)) {
          invoices.push(invoice);
          seenInvoiceNumbers.add(invoice.number);
        }
      }
    }

    for (const invoice of this.invoicesByTransactionHash[transaction.source?.sha1] || []) {
      if (!seenInvoiceNumbers.has(invoice.number)) {
        invoices.push(invoice);
        seenInvoiceNumbers.add(invoice.number);
      }
    }

    return invoices;
  }

  private mapInvoicesToTransactions(invoices: InvoiceInterface[], transactions: Transaction[]) {
    const byHash: { [transactionHash: string]: InvoiceInterface[] } = {};
    const transactionsByInvoiceNumber = new Map<number, string[]>();

    for (const transaction of transactions) {
      if (!transaction.source?.sha1 || !transaction.invoice?.number) {
        continue;
      }

      const hashes = transactionsByInvoiceNumber.get(transaction.invoice.number) || [];
      hashes.push(transaction.source.sha1);
      transactionsByInvoiceNumber.set(transaction.invoice.number, hashes);
    }

    for (const invoice of invoices) {
      for (const payment of invoice.paymentsMade || []) {
        if (payment.transactionSha1) {
          byHash[payment.transactionSha1] = [...(byHash[payment.transactionSha1] || []), invoice];
        }
      }

      for (const transactionHash of transactionsByInvoiceNumber.get(invoice.number) || []) {
        byHash[transactionHash] = [...(byHash[transactionHash] || []), invoice];
      }

      byHash[`invoice:${invoice.number}`] = [...(byHash[`invoice:${invoice.number}`] || []), invoice];
    }

    return byHash;
  }

  getInvoiceMatchCount() {
    return (this.dataSource.data || []).reduce((count, transaction) => count + this.getTransactionInvoices(transaction).length, 0);
  }

  private createEmptySummary(): WeeklyReportSummary {
    return {
      revenue: 0,
      revenueGst: 0,
      expenses: 0,
      expensesGst: 0,
      wages: 0,
      profit: 0,
      transactionCount: 0
    };
  }

  private getTuesday(date: Date) {
    const result = new Date(date);
    const daysSinceTuesday = (result.getDay() + 5) % 7;
    result.setDate(result.getDate() - daysSinceTuesday);
    result.setHours(0, 0, 0, 0);
    return result;
  }

  private getMonday(date: Date) {
    const result = this.addDays(date, 6);
    result.setHours(23, 59, 59, 999);
    return result;
  }

  private addDays(date: Date, days: number) {
    const result = new Date(date);
    result.setDate(result.getDate() + days);
    return result;
  }

  private getTime(date: Date | string) {
    return new Date(date).getTime();
  }

  private formatDate(date: Date | string) {
    const value = new Date(date);
    return value.toLocaleDateString('en-AU', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  }

  private formatMoney(amount: number) {
    return new Intl.NumberFormat('en-AU', {
      style: 'currency',
      currency: 'AUD'
    }).format((amount || 0) / 100);
  }

  private cleanClipboardText(value: string) {
    return (value || '').replace(/\s+/g, ' ').replace(/\|/g, '-').trim();
  }

  private getInvoiceNetTotal(invoice: InvoiceInterface) {
    return invoice.isGSTIncluded ? (invoice.total || 0) - (invoice.gst || 0) : (invoice.subTotal || invoice.total || 0);
  }

  private getInvoiceNumberFromDescription(input: string): number | null {
    if (!input) {
      return null;
    }

    const matches = input.match(/\b\d{3,6}\b/g) || [];
    const candidates = matches
      .map(match => Number(match))
      .filter(number => !isNaN(number) && number > 0);

    if (!candidates.length) {
      return null;
    }

    return candidates.find(number => number >= 1000 && number <= 9999) || candidates[0];
  }

  private formatLineItem(lineItem: LineItem, invoice: InvoiceInterface) {
    const itemCode = this.cleanClipboardText(lineItem.item || '');
    const description = this.cleanClipboardText(lineItem.desc || '');
    const quantity = lineItem.qty || 0;
    const price = this.getLineItemNetPrice(lineItem, invoice);
    const total = quantity * price;
    const parts = [];

    if (itemCode) {
      parts.push(itemCode);
    }

    if (description) {
      parts.push(description);
    }

    parts.push(`qty ${quantity}`);
    parts.push(`each net ${this.formatMoney(price)}`);
    parts.push(`line net total ${this.formatMoney(total)}`);

    return parts.join(' | ');
  }

  private getLineItemNetPrice(lineItem: LineItem, invoice: InvoiceInterface) {
    const price = lineItem.price || 0;
    return invoice.isGSTIncluded ? Math.round(price - (price / 11)) : price;
  }
}
